/**
 * Country Store
 *
 * Manages country/region state including:
 * - Current country selection
 * - Country configuration
 * - Geolocation detection
 *
 * @module @schoolidb/store/countryStore
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CountryCode } from '@schoolidb/types';
import { getCountryConfig, type CountryConfig } from '@schoolidb/config';

// ============================================================================
// Types
// ============================================================================

/**
 * Country detection source
 */
export type DetectionSource = 'manual' | 'geolocation' | 'ip' | 'default';

/**
 * Country state
 */
export interface CountryState {
  /** Current country code */
  countryCode: CountryCode;
  /** Country configuration */
  config: CountryConfig;
  /** How the country was detected */
  detectionSource: DetectionSource;
  /** Whether detection is in progress */
  isDetecting: boolean;
  /** Last detection timestamp */
  lastDetected: number | null;
}

/**
 * Country actions
 */
export interface CountryActions {
  /** Set country manually */
  setCountry: (countryCode: CountryCode) => void;
  /** Set country from detection */
  setDetectedCountry: (countryCode: CountryCode, source: DetectionSource) => void;
  /** Start detection */
  startDetection: () => void;
  /** End detection */
  endDetection: () => void;
  /** Get current country config */
  getConfig: () => CountryConfig;
}

export type CountryStore = CountryState & CountryActions;

// ============================================================================
// Initial State
// ============================================================================

const defaultCountry: CountryCode = 'IN';

const initialState: CountryState = {
  countryCode: defaultCountry,
  config: getCountryConfig(defaultCountry),
  detectionSource: 'default',
  isDetecting: false,
  lastDetected: null,
};

// ============================================================================
// Store
// ============================================================================

/**
 * Country store with persistence
 */
export const useCountryStore = create<CountryStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setCountry: (countryCode) => {
        set({
          countryCode,
          config: getCountryConfig(countryCode),
          detectionSource: 'manual',
        });
      },

      setDetectedCountry: (countryCode, source) => {
        set({
          countryCode,
          config: getCountryConfig(countryCode),
          detectionSource: source,
          lastDetected: Date.now(),
          isDetecting: false,
        });
      },

      startDetection: () => {
        set({ isDetecting: true });
      },

      endDetection: () => {
        set({ isDetecting: false });
      },

      getConfig: () => {
        return get().config;
      },
    }),
    {
      name: 'schoolidb-country',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        countryCode: state.countryCode,
        detectionSource: state.detectionSource,
        lastDetected: state.lastDetected,
      }),
      onRehydrateStorage: () => (state) => {
        // Rehydrate config from country code
        if (state) {
          state.config = getCountryConfig(state.countryCode);
        }
      },
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectCountryCode = (state: CountryStore) => state.countryCode;
export const selectCountryConfig = (state: CountryStore) => state.config;
export const selectDetectionSource = (state: CountryStore) => state.detectionSource;
export const selectIsDetecting = (state: CountryStore) => state.isDetecting;
